import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import Header from './_components/Header/Header';
import Sidebar from './_components/Sidebar/Sidebar';
import Footer from './_components/Footer/Footer';
import EventsList from './_pages/EventsList';
import EventScreen from './_pages/events/EventScreen';
import UpgradePage from './_pages/billing/UpgradePage';
import PaymentSuccess from './_pages/billing/PaymentSuccess';
import { eventsApi } from './_lib/api-client';
import type { Event } from './_graphql/generated/types';

// Layout component that includes sidebar
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [currentEventId, setCurrentEventId] = useState<string | null>(null);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  
  // Determine current tab from URL
  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get('tab') as 'guests' | 'invitations' | 'analytics' | null;
  
  // Check if we're on dashboard (events list)
  const isOnDashboard = location.pathname === '/app/events' || location.pathname === '/app';
  
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const allEvents = await eventsApi.getAll();
        setEvents(allEvents);
        
        // If we're on an event page, find the current event
        if (params.eventCode) {
          const event = allEvents.find(e => e.code === params.eventCode);
          if (event) {
            setCurrentEventId(event.id);
            setCurrentEvent(event);
          }
        } else {
          setCurrentEvent(null);
        }
      } catch (error) {
        console.error('Failed to fetch events:', error);
      }
    };
    fetchEvents();
  }, [params.eventCode]);
  
  const handleEventChange = (eventId: string) => {
    const event = events.find(e => e.id === eventId);
    if (event) {
      const eventNameSlug = event.eventName.toLowerCase().replace(/\s+/g, '-');
      window.location.href = `/app/events/${event.code}/${eventNameSlug}`;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.15)', backdropFilter: 'blur(2px)' }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed lg:fixed lg:translate-x-0 z-50 transition-transform duration-300 ease-in-out`}>
        <Sidebar 
          onClose={() => setIsMobileMenuOpen(false)}
          events={events}
          currentEventId={currentEventId}
          onEventChange={handleEventChange}
          currentTab={currentTab || undefined}
          isOnDashboard={isOnDashboard}
        />
      </div>
      
      <div className="flex-1 flex flex-col min-w-0 lg:ml-72">
        <Header onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
        <main className="flex-1 flex flex-col">
          {children}
        </main>
        <Footer />
      </div>
    </div>
  );
};

// Root redirect component
const RootRedirect: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [eventCount, setEventCount] = useState(0);
  
  useEffect(() => {
    const checkEvents = async () => {
      try {
        const events = await eventsApi.getAll();
        setEventCount(events.length);
      } catch (error) {
        console.error('Failed to fetch events:', error);
      } finally {
        setLoading(false);
      }
    };
    checkEvents();
  }, []);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }
  
  // Always show events list (even if only one event)
  return <Navigate to="events" replace />;
};

const App: React.FC = () => {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<Layout><RootRedirect /></Layout>} />
      
      {/* Events routes */}
      <Route path="events" element={<Layout><EventsList /></Layout>} />
      <Route path="events/:eventCode/:eventName?" element={<Layout><EventScreen /></Layout>} />
      
      {/* Billing routes */}
      <Route path="billing/upgrade" element={<Layout><UpgradePage /></Layout>} />
      <Route path="billing/success" element={<Layout><PaymentSuccess /></Layout>} />
      
      {/* Catch all - redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;